import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsAPI, type ItemDetailsModifier } from '@matix/api-client';
import {
  ReportToolbar, Field, DateTimeRange, fmt3, fmtPct,
  todayFrom, todayTo, rangeParams, rangeLabel, useLabelOf, printableHtml,
} from './reportShared';
import { MultiSelect } from './MultiSelect';
import { printReport, type ReportPaper } from './reportPrint';
import { ReportSheetBuilder, downloadReportExcel, type ExcelRow } from './reportExcel';
import { useLang } from '../../lib/language';

export function ItemDetailsReport(): JSX.Element {
  const { t, lang } = useLang();
  const labelOf = useLabelOf();
  const printRef = useRef<HTMLDivElement>(null);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [cashierId, setCashierId] = useState<number | ''>('');
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [from, setFrom] = useState(todayFrom);
  const [to, setTo] = useState(todayTo);
  const [params, setParams] = useState<
    {
      branch_id?: number; cashier_id?: number; from: string; to: string;
      from_time?: string; to_time?: string; category_ids?: string;
    } | null
  >(null);

  // Filters depend on the selected branch (cashiers + categories are per-branch).
  const filtersQ = useQuery({
    queryKey: ['rpt-filters', branchId === '' ? null : branchId],
    queryFn: () => reportsAPI.filters(branchId === '' ? undefined : branchId),
  });
  // Own branch only, preselected, no "All" (2026-09-08) — the server pins the
  // query to it as well. See QuickSalesReport for the rationale.
  const ownBranchId = filtersQ.data?.default_branch_id ?? null;
  const branches = useMemo(
    () => (filtersQ.data?.branches ?? []).filter((b) => ownBranchId == null || b.id === ownBranchId),
    [filtersQ.data, ownBranchId],
  );
  useEffect(() => {
    if (filtersQ.data && branchId === '') {
      setBranchId(ownBranchId ?? branches[0]?.id ?? '');
    }
  }, [filtersQ.data, branchId, ownBranchId, branches]);

  const dataQ = useQuery({
    queryKey: ['item-details', params],
    queryFn: () => reportsAPI.itemDetails(params!),
    enabled: params != null,
  });

  const range = rangeLabel(from, to);
  const title = t({ en: 'Sales by Items Report', ar: 'تقرير المبيعات بالاصناف' });

  function run(): void {
    const r = rangeParams(from, to);
    if (!r) return;
    setParams({
      ...(branchId !== '' ? { branch_id: branchId } : {}),
      ...(cashierId !== '' ? { cashier_id: cashierId } : {}),
      ...r,
      ...(categoryIds.length > 0 ? { category_ids: categoryIds.join(',') } : {}),
    });
  }
  function exportPdf(paper: ReportPaper): void { printReport(title, range, printableHtml(printRef), paper); }

  const d = dataQ.data;
  const branchName = filtersQ.data?.branches.find((b) => b.id === branchId);
  const scope = labelOf(branchName ?? { name: t({ en: 'All branches', ar: 'كل الفروع' }), name_ar: null });

  function exportExcel(): void {
    if (!d) return;
    const b = new ReportSheetBuilder().title(title, `${scope} · ${range}`);
    const cols = [
      { label: t({ en: 'Item', ar: 'الصنف' }) },
      { label: t({ en: 'Qty', ar: 'الكمية' }), kind: 'qty' as const },
      { label: t({ en: 'Value', ar: 'القيمة' }), kind: 'qty' as const },
      { label: '%', kind: 'pct' as const },
    ];
    for (const cat of d.categories) {
      const rows: ExcelRow[] = [];
      for (const it of cat.items) {
        rows.push([it.item_name || it.item_name_ar, it.qty, it.amount, it.pct]);
        // The options chosen inside the item, as the screen shows them: one
        // note line under the item, never rows of their own — an option is
        // part of what was already charged, not a sale beside it.
        if (it.modifiers.length > 0) rows.push({ note: modifierLine(it.modifiers, labelOf, t) });
      }
      b.section(labelOf({ name: cat.category_name, name_ar: cat.category_name_ar }))
        .table(cols, rows, {
          totals: [t({ en: 'Total', ar: 'الإجمالي' }), cat.total_qty, cat.total_amount, 100],
        });
    }
    if (d.categories.length > 0) {
      b.table(cols, [], {
        header: false,
        totals: [t({ en: 'Grand Total', ar: 'الإجمالي الكلي' }), d.grand_total_qty, d.grand_total_amount, null],
      });
    } else {
      b.table(cols, [], { emptyText: t({ en: 'No data for these filters.', ar: 'لا توجد بيانات لهذه المرشحات.' }) });
    }
    downloadReportExcel(`${title} ${range}`, b.build(title, { rightToLeft: lang === 'ar' }));
  }

  return (
    <div className="rpt-wrap">
      <ReportToolbar
        running={dataQ.isFetching}
        canExport={!!d}
        onRun={run}
        onExport={exportPdf}
        onExportExcel={exportExcel}
        filters={
          <>
            <Field label={t({ en: 'Branch', ar: 'الفرع' })}>
              <select value={branchId} onChange={(e) => { setBranchId(e.target.value ? Number(e.target.value) : ''); setCashierId(''); setCategoryIds([]); }}>
                {branches.map((b) => <option key={b.id} value={b.id}>{labelOf(b)}</option>)}
              </select>
            </Field>
            <Field label={t({ en: 'Cashier', ar: 'الكاشير' })}>
              <select value={cashierId} onChange={(e) => setCashierId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">{t({ en: '— All —', ar: '— الكل —' })}</option>
                {filtersQ.data?.cashiers.map((c) => <option key={c.id} value={c.id}>{labelOf(c)}</option>)}
              </select>
            </Field>
            <DateTimeRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
            <Field label={t({ en: 'Categories (blank = all)', ar: 'الفئات (فارغ = الكل)' })}>
              <MultiSelect
                options={(filtersQ.data?.categories ?? []).map((c) => ({ id: c.id, label: labelOf(c) }))}
                value={categoryIds}
                onChange={setCategoryIds}
                placeholder={t({ en: 'All categories', ar: 'كل الفئات' })}
                minWidth={200}
              />
            </Field>
          </>
        }
      />

      {dataQ.error && <div className="rpt-error">{t({ en: 'Failed to load report. Try again.', ar: 'تعذّر تحميل التقرير. حاول مجدداً.' })}</div>}

      <div className="rpt-print" ref={printRef}>
        {d && (
          <>
            <div className="rpt-meta">{scope} · {range}</div>
            {d.categories.length === 0 && <div className="rpt-empty">{t({ en: 'No data for these filters.', ar: 'لا توجد بيانات لهذه المرشحات.' })}</div>}
            {d.categories.map((cat) => (
              <div key={cat.category_id}>
                <h2 className="section">
                  {labelOf({ name: cat.category_name, name_ar: cat.category_name_ar })}
                </h2>
                <table>
                  <thead><tr><th>{t({ en: 'Item', ar: 'الصنف' })}</th><th className="num">{t({ en: 'Qty', ar: 'الكمية' })}</th><th className="num">{t({ en: 'Value', ar: 'القيمة' })}</th><th className="num">%</th></tr></thead>
                  <tbody>
                    {cat.items.map((it) => (
                      <Fragment key={it.item_id}>
                        <tr>
                          <td>{it.item_name || it.item_name_ar}</td>
                          <td className="num">{fmt3(it.qty)}</td>
                          <td className="num">{fmt3(it.amount)}</td>
                          <td className="num">{fmtPct(it.pct)}</td>
                        </tr>
                        {/*
                          What was chosen inside this item — the combo boxes are
                          the whole point of the report for a tenant that sells
                          them, and the money columns deliberately stay on the
                          item row: an option is part of what was already
                          charged, never a sale of its own.
                        */}
                        {it.modifiers.length > 0 && (
                          <tr className="rpt-subrow">
                            <td colSpan={4}>
                              <ModifierBreakdown modifiers={it.modifiers} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td>{t({ en: 'Total', ar: 'الإجمالي' })}</td><td className="num">{fmt3(cat.total_qty)}</td><td className="num">{fmt3(cat.total_amount)}</td><td className="num">100.00%</td></tr>
                  </tfoot>
                </table>
              </div>
            ))}
            {d.categories.length > 0 && (
              <table style={{ marginTop: 8 }}>
                <tfoot><tr className="total-row"><td>{t({ en: 'Grand Total', ar: 'الإجمالي الكلي' })}</td><td className="num">{fmt3(d.grand_total_qty)}</td><td className="num">{fmt3(d.grand_total_amount)}</td></tr></tfoot>
              </table>
            )}
          </>
        )}
        {!d && !dataQ.isFetching && <div className="rpt-empty">{t({ en: 'Choose filters, then “Show report”.', ar: 'اختر المرشحات، ثم “عرض التقرير”.' })}</div>}
      </div>
    </div>
  );
}

/**
 * The options chosen inside one item, grouped by the modifier group they came
 * from ("Select Your Sandwiches: Halloumi ×12, Turkey ×8").
 *
 * Rendered as text in a spanning sub-row rather than as a nested table: this
 * report prints to a 72mm till roll as well as to A4, and a table inside a
 * table is the one thing that reliably breaks that layout.
 *
 * Names come from whichever store the sale was rung through — ours keeps one
 * language, the ERP's carries Arabic on some rows — so `labelOf` picks per
 * option rather than per report.
 */
function ModifierBreakdown({ modifiers }: { modifiers: ItemDetailsModifier[] }): JSX.Element {
  const { t } = useLang();
  const labelOf = useLabelOf();
  const groups = groupModifiers(modifiers, t);
  return (
    <div className="rpt-mods">
      {[...groups.entries()].map(([group, rows]) => (
        <div key={group} className="rpt-mods-group">
          <span className="rpt-mods-label">{group}:</span>{' '}
          {rows.map((m, i) => (
            <span key={`${m.name}-${i}`} className="rpt-mods-item">
              {labelOf({ name: m.name, name_ar: m.name_ar })}
              <span className="rpt-mods-qty"> ×{fmt3(m.qty)}</span>
              {i < rows.length - 1 ? <span className="rpt-mods-sep"> · </span> : null}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

type Translate = <T,>(opts: { en: T; ar: T }) => T;
type LabelOf = (item: { name: string; name_ar: string | null }) => string;

/** The chosen options bucketed by the group they came from, in arrival order. */
function groupModifiers(
  modifiers: ItemDetailsModifier[],
  t: Translate,
): Map<string, ItemDetailsModifier[]> {
  const groups = new Map<string, ItemDetailsModifier[]>();
  for (const m of modifiers) {
    const key = m.group_name || t({ en: 'Options', ar: 'الخيارات' });
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(m);
  }
  return groups;
}

/**
 * The same breakdown as one line of plain text, for the spreadsheet — a
 * spanning note cell under the item, the way the roll printout stacks it
 * under the card. A cell cannot hold the screen's markup, and splitting the
 * options into rows of their own would read as sales they are not.
 */
export function modifierLine(
  modifiers: ItemDetailsModifier[],
  labelOf: LabelOf,
  t: Translate,
): string {
  return [...groupModifiers(modifiers, t).entries()]
    .map(([group, rows]) => `${group}: ${rows
      .map((m) => `${labelOf({ name: m.name, name_ar: m.name_ar })} ×${fmt3(m.qty)}`)
      .join(' · ')}`)
    .join('\n');
}
